// hl-custom: custom file, not in stock Horizon -- grouped product-type filter: expand/collapse + select-all parent behavior for grouped list filters
import { Component } from '@theme/component';

/**
 * @typedef {Object} FilterGroupRefs
 * @property {HTMLInputElement} parentInput - The select-all checkbox (no name attribute)
 * @property {HTMLButtonElement} toggle - The expand/collapse button carrying aria-expanded
 * @property {HTMLElement} children - The <ul> wrapping the child filter inputs
 */

/**
 * A parent group inside a grouped list filter. Manages expand/collapse, a "select all"
 * checkbox, and the parent's checked/indeterminate state derived from its children.
 *
 * @extends {Component<FilterGroupRefs>}
 */
class FilterGroup extends Component {
  requiredRefs = ['parentInput', 'toggle', 'children'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.parentInput.addEventListener('change', this.#onParentChange);
    this.refs.toggle.addEventListener('click', this.#onToggle);
    this.refs.children.addEventListener('change', this.#syncParentState);
    this.#syncParentState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.parentInput?.removeEventListener('change', this.#onParentChange);
    this.refs.toggle?.removeEventListener('click', this.#onToggle);
    this.refs.children?.removeEventListener('change', this.#syncParentState);
  }

  /**
   * Re-derive the parent state after the Section Rendering API + morph re-render this group.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncParentState();
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #childInputs() {
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.refs.children?.querySelectorAll('input[type="checkbox"]') ?? [])
    );
  }

  #anyActive() {
    return this.#childInputs().some((input) => input.checked);
  }

  /**
   * @param {boolean} open
   */
  #expand(open) {
    this.refs.toggle.setAttribute('aria-expanded', String(open));
    this.refs.children.hidden = !open;
  }

  /**
   * The parent checkbox fired its own change. Stop it from reaching facet-inputs-component
   * (it has no name and must not submit on its own), mirror its state onto the children,
   * then dispatch a single bubbling change so the existing /updateFilters runs exactly once.
   *
   * @param {Event} event
   */
  #onParentChange = (event) => {
    event.stopPropagation();
    const { checked } = this.refs.parentInput;
    let changed = false;

    for (const input of this.#childInputs()) {
      if (input.disabled) continue;
      if (input.checked !== checked) {
        input.checked = checked;
        changed = true;
      }
    }

    this.#expand(checked || this.#anyActive());

    if (changed) {
      this.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.#syncParentState();
    }
  };

  #onToggle = () => {
    const expanded = this.refs.toggle.getAttribute('aria-expanded') === 'true';
    this.#expand(!expanded);
  };

  /**
   * Derive parent checked/indeterminate from the children. indeterminate is a JS-only
   * property, so it must be reapplied here (it cannot be expressed as an HTML attribute).
   */
  #syncParentState = () => {
    const inputs = this.#childInputs();
    const total = inputs.length;
    const active = inputs.filter((input) => input.checked).length;
    const parent = this.refs.parentInput;

    parent.checked = total > 0 && active === total;
    parent.indeterminate = active > 0 && active < total;
  };
}

if (!customElements.get('filter-group')) {
  customElements.define('filter-group', FilterGroup);
}
